import { useEffect, useRef, useState } from 'react'
import { Platform, View } from 'react-native'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { RegionPrefixes } from '@ca/common'
import type { Customer, VerifyStrategyType } from '@ca/common/types'
import FormInput from '@ui/forms/Input/FormInput'
import Button from '@ui/buttons/Button'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import { contactStepSchema } from '../stepSchemas'
import useCreateCustomerStore from '../useCreateCustomerStore'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useToast } from '@ui/toasts/useToastManager'
import { trpc } from 'api'
import { isKidsAccount, clearParentAuth } from '../utils'
import { useStaffLoginCustomer } from 'hooks/useStaffLoginCustomer'
import CustomerAuthModal from '@user/CustomerAuthModal'
import StaffCustomerLoginModal from '@user/StaffCustomerLoginModal'
import ParentAuthModal from '../ParentAuthModal'
import ExistingAccountFoundModal from '../ExistingAccountFoundModal'
import MultipleAccountsFoundModal from '../MultipleAccountsFoundModal'
import ParentAccountFoundModal from '../ParentAccountFoundModal'

type ExistsResult = {
  email: boolean
  mobile: boolean
  conflict: boolean
}

const getFindAccountInput = (
  values: { email?: string; mobile?: string },
  matchResult: ExistsResult
) => {
  if (matchResult.email && matchResult.mobile && !matchResult.conflict) {
    return { email: values.email, mobile: values.mobile }
  }
  if (matchResult.email) {
    return { email: values.email }
  }
  return { mobile: values.mobile }
}

interface Props {
  submitRef: React.MutableRefObject<() => void>
  onSuccess: () => void
  setIsPending?: (v: boolean) => void
}

type Mode = 'adult' | 'parent'

export default function ContactStep({
  submitRef,
  onSuccess,
  setIsPending,
}: Props) {
  const { t } = useTranslation(['user', 'modals', 'toasts', 'auth'])
  const { data, updateData } = useCreateCustomerStore()
  const config = useRemoteConfig()
  const toast = useToast()
  const prefix =
    RegionPrefixes[config?.region.countryCode || 'de'] || RegionPrefixes.de

  const isUnder18 = isKidsAccount(data)

  const [existingFoundOpen, setExistingFoundOpen] = useState(false)
  const [multipleAccountsOpen, setMultipleAccountsOpen] = useState(false)
  const [matchResult, setMatchResult] = useState<ExistsResult | null>(null)
  const [parentFoundOpen, setParentFoundOpen] = useState(false)
  const [pendingParentCustomer, setPendingParentCustomer] =
    useState<Customer | null>(null)
  const [authCustomer, setAuthCustomer] = useState<Customer | null>(null)
  const [parentCustomer, setParentCustomer] = useState<Customer | null>(null)
  const [authMode, setAuthMode] = useState<Mode>('adult')
  const [showLandline, setShowLandline] = useState(!!data.phone)
  const skipNextCloseAdvanceRef = useRef(false)

  const findParentAccount = trpc.customer.findParentAccount.useMutation()
  const existsCheck = trpc.customer.exists.useMutation()

  const adultVerify = useStaffLoginCustomer()
  const parentVerify = useStaffLoginCustomer()

  const form = useForm({
    resolver: zodResolver(contactStepSchema),
    defaultValues: {
      email: data.email || '',
      mobile: data.mobile || '',
      phone: data.phone || '',
    },
  })

  const handleExistsResult = (
    res: ExistsResult,
    values: { email?: string; mobile?: string },
    onMatch: () => void
  ) => {
    setIsPending?.(false)

    if (!res.email && !res.mobile) {
      onMatch()
      return
    }

    if (res.conflict) {
      setMatchResult(res)
      setMultipleAccountsOpen(true)
      return
    }

    setMatchResult(res)
    onMatch()
  }

  const handleAdultExistsCheck = (values: {
    email?: string
    mobile?: string
  }) => {
    existsCheck.mutate(
      { email: values.email, mobile: values.mobile },
      {
        onSuccess: (res) => {
          handleExistsResult(res, values, () => {
            if (!res.email && !res.mobile) {
              onSuccess()
              return
            }
            setExistingFoundOpen(true)
          })
        },
        onError: (err) => {
          setIsPending?.(false)
          toast({
            title: t('toasts:error.default.title'),
            text: err.message,
          })
        },
      }
    )
  }

  const proceedWithParentAccount = (
    values: { email?: string; mobile?: string },
    result: ExistsResult
  ) => {
    findParentAccount.mutate(getFindAccountInput(values, result), {
      onSuccess: (customer) => {
        setIsPending?.(false)
        updateData({
          parentAuthStatus: 'pending',
          parentData: undefined,
        })
        setPendingParentCustomer(customer)
        setParentFoundOpen(true)
      },
      onError: (err) => {
        setIsPending?.(false)

        if (err.data?.code === 'NOT_FOUND') {
          updateData(clearParentAuth())
          onSuccess()
          return
        }
        toast({
          title: t('toasts:error.default.title'),
          text: err.message,
        })
      },
    })
  }

  const handleUnder18Check = (values: { email?: string; mobile?: string }) => {
    if (
      data.parentAuthStatus === 'verified' ||
      data.parentAuthStatus === 'skipped'
    ) {
      setIsPending?.(false)
      onSuccess()
      return
    }

    existsCheck.mutate(
      { email: values.email, mobile: values.mobile },
      {
        onSuccess: (res) => {
          handleExistsResult(res, values, () => {
            if (!res.email && !res.mobile) {
              onSuccess()
              return
            }
            proceedWithParentAccount(values, res)
          })
        },
        onError: (err) => {
          setIsPending?.(false)
          toast({
            title: t('toasts:error.default.title'),
            text: err.message,
          })
        },
      }
    )
  }

  const handleSubmitValues = (values: {
    email?: string
    mobile?: string
    phone?: string
  }) => {
    const contactChanged =
      values.email !== data.email ||
      values.mobile !== data.mobile ||
      values.phone !== data.phone

    if (!isUnder18) {
      updateData({
        ...values,
        ...clearParentAuth(),
      })
    } else if (contactChanged) {
      updateData({
        ...values,
        ...clearParentAuth(),
      })
    } else {
      updateData(values)
    }

    if (showLandline) {
      onSuccess()
      return
    }

    if (!isUnder18) {
      handleAdultExistsCheck(values)
      return
    }

    handleUnder18Check(values)
  }

  useEffect(() => {
    submitRef.current = form.handleSubmit(handleSubmitValues, () =>
      setIsPending?.(false)
    )
  })

  const mobileBlur = () => {
    if (form.getValues().mobile?.trim() === prefix) {
      form.setValue('mobile', '')
    }
  }

  const mobileFocus = () => {
    const val = form.getValues().mobile?.trim()
    if (val === prefix || val === '') {
      form.setValue('mobile', `${prefix} `)
    }
  }

  const handleExistingSignIn = () => {
    if (!matchResult) {
      return
    }

    const values = form.getValues()
    skipNextCloseAdvanceRef.current = true
    setExistingFoundOpen(false)
    findParentAccount.mutate(getFindAccountInput(values, matchResult), {
      onSuccess: (customer) => {
        setAuthMode('adult')
        setAuthCustomer(customer)
      },
      onError: (err) => {
        toast({
          title: t('toasts:error.default.title'),
          text: err.message,
        })
      },
    })
  }

  const handleConflictUseEmail = () => {
    setMultipleAccountsOpen(false)
    setMatchResult(null)
    form.setValue('mobile', '')
    updateData({ mobile: '' })
  }

  const handleConflictUseMobile = () => {
    setMultipleAccountsOpen(false)
    setMatchResult(null)
    form.setValue('email', '')
    updateData({ email: '' })
  }

  const handleExistingClose = () => {
    if (skipNextCloseAdvanceRef.current) {
      skipNextCloseAdvanceRef.current = false
      return
    }
    setExistingFoundOpen(false)
    setMatchResult(null)
  }

  const handleExistingCancel = () => {
    setExistingFoundOpen(false)
    setMatchResult(null)
  }

  const handleParentFoundAuthenticate = () => {
    if (!pendingParentCustomer) {
      return
    }
    setParentFoundOpen(false)
    updateData({
      parentAuthStatus: 'required',
      parentData: undefined,
    })
    setAuthMode('parent')
    setAuthCustomer(pendingParentCustomer)
    setPendingParentCustomer(null)
  }

  const handleParentFoundSkip = () => {
    setParentFoundOpen(false)
    setPendingParentCustomer(null)
    updateData({
      parentAuthStatus: 'skipped',
      parentData: undefined,
    })
    onSuccess()
  }

  const handleParentFoundClose = () => {
    setParentFoundOpen(false)
    setPendingParentCustomer(null)
  }

  const markParentAuthFailed = () => {
    updateData({
      parentAuthStatus: 'failed',
      parentData: undefined,
    })
    parentVerify.cancel()
    setParentCustomer(null)
    setAuthCustomer(null)
  }

  useEffect(() => {
    if (adultVerify.result) {
      setAuthCustomer(null)
    }
  }, [adultVerify.result])

  useEffect(() => {
    if (parentVerify.result) {
      setAuthCustomer(null)
    }
  }, [parentVerify.result])

  const handleSelectStrategy = (
    customer: Customer,
    strategy: VerifyStrategyType
  ) => {
    if (authMode === 'parent') {
      setParentCustomer(customer)
      parentVerify.request(customer, strategy)
    } else {
      adultVerify.request(customer, strategy)
    }
  }

  const handleParentFetched = (customer: Customer) => {
    updateData({
      parentAuthStatus: 'verified',
      parentData: {
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email ?? null,
        mobile: customer.mobile ?? null,
        street: customer.address?.street ?? null,
        street2: customer.address?.street2 ?? null,
        city: customer.address?.city ?? null,
        zip: customer.address?.zip ?? null,
        country_code: customer.address?.country_code,
        birth_date: customer.birth_date ?? null,
      },
    })
    parentVerify.cancel()
    setParentCustomer(null)
    setAuthCustomer(null)
    onSuccess()
  }

  const handleParentClose = () => {
    if (data.parentAuthStatus === 'required') {
      markParentAuthFailed()
      return
    }
    parentVerify.cancel()
    setParentCustomer(null)
  }

  const handleParentAuthClose = () => {
    if (authMode === 'parent' && parentVerify.result) {
      setAuthCustomer(null)
      return
    }

    if (authMode === 'adult' && adultVerify.result) {
      setAuthCustomer(null)
      return
    }

    if (authMode === 'parent' && data.parentAuthStatus === 'required') {
      markParentAuthFailed()
      return
    }

    setAuthCustomer(null)

    if (authMode === 'parent') {
      parentVerify.cancel()
    }
    if (authMode === 'adult') {
      adultVerify.cancel()
    }
  }

  return (
    <>
      <View className="overflow-hidden rounded-xl border border-black/10 bg-white">
        <FormProvider {...form}>
          <View className="gap-8 p-8">
            {showLandline ? (
              <FormInput
                control={form.control}
                name="phone"
                label={t('user:landline')}
                labelVariant="top"
                required
                inputMode="numeric"
              />
            ) : (
              <>
                <FormInput
                  control={form.control}
                  name="email"
                  label={t('user:email')}
                  labelVariant="top"
                  required
                  autoCorrect={false}
                  autoCapitalize="none"
                  inputMode={Platform.select({ web: 'text', default: 'email' })}
                />
                <FormInput
                  control={form.control}
                  name="mobile"
                  label={t('user:mobile')}
                  labelVariant="top"
                  required
                  inputMode="numeric"
                  onFocus={mobileFocus}
                  onBlur={mobileBlur}
                />
              </>
            )}
          </View>
        </FormProvider>
      </View>

      {!isUnder18 && (
        <View className="items-center pt-8">
          <Button
            variant="outlined"
            size="xs"
            overrideColorFront={colors.black50}
            label={
              <Typography variant="XSB" color={colors.black50}>
                {showLandline
                  ? t('auth:create.landline.back')
                  : t('auth:create.contactDetails.neither')}
              </Typography>
            }
            onPress={() => {
              setShowLandline((v) => !v)
              form.setValue('email', '')
              form.setValue('mobile', '')
              form.setValue('phone', '')
            }}
          />
        </View>
      )}

      <ExistingAccountFoundModal
        open={existingFoundOpen}
        onClose={handleExistingClose}
        onSignIn={handleExistingSignIn}
        onCancel={handleExistingCancel}
      />

      <MultipleAccountsFoundModal
        open={multipleAccountsOpen}
        onClose={() => setMultipleAccountsOpen(false)}
        onUseEmail={handleConflictUseEmail}
        onUseMobile={handleConflictUseMobile}
      />

      <ParentAccountFoundModal
        open={parentFoundOpen}
        onClose={handleParentFoundClose}
        onAuthenticate={handleParentFoundAuthenticate}
        onSkip={handleParentFoundSkip}
      />

      <CustomerAuthModal
        customer={authCustomer}
        onClose={handleParentAuthClose}
        onSelectStrategy={handleSelectStrategy}
        isVerifying={
          authMode === 'parent' ? parentVerify.isLoading : adultVerify.isLoading
        }
        hideStaffStrategy
        headerBadges={
          authMode === 'parent'
            ? [
                {
                  text: t('modals:parentAuth.parentBadge'),
                  variant: 'warning',
                },
              ]
            : undefined
        }
      />

      <StaffCustomerLoginModal
        verify={adultVerify}
        onSuccess={() => adultVerify.cancel()}
      />

      <ParentAuthModal
        customer={parentCustomer}
        verifyRes={parentVerify.result ?? null}
        onClose={handleParentClose}
        onSuccess={handleParentFetched}
      />
    </>
  )
}
